import Cookies from 'js-cookie';
import { useEffect, useState } from 'react'
import useDelete from '../../../components/hooks/useDelete';
import { delete_RdRentalCreditCard, getRdRentalCreditCardWithTenantId, put_RdRentalCreditCard } from '../../../services/GroupCreditCard';
import Pagination from '../../../components/Pagination';
import useAuth from '../../../components/hooks/useAuth';
import { useTheme } from '../../../components/hooks/useTheme';
import useTenant from '../../../components/hooks/useTenant';

const heading = ["#", "Cardholder Name", "Exp. Date", "Last 4 Digits", "Active"];

type CreditCardDetailsProps = {
    isRefresh: boolean;
    setIsRefresh: (value: boolean) => void;
    setShowPopupAddNewCreditCard: (value: boolean) => void;
}

const CreditCardDetails = ({
    isRefresh,
    setIsRefresh,
    setShowPopupAddNewCreditCard
}: CreditCardDetailsProps) => {
    const tenantId = Cookies.get("tenantId-jj-rental-ui") || "";
    const { handleTenantError }: any = useTenant();
    const { auth }: any = useAuth();
    const { setDeletePopup, setData: setDeleteData }: any = useDelete();
    const [creditCardData, setCreditCardData] = useState<any[]>([]);
    const [paginationModel, setPaginationModel] = useState<{ page: number, size: number }>({
        page: 0,
        size: 5
    });
    const [totalLength, setTotalLength] = useState<number>(0);
    const [showEntries, setShowEntries] = useState<number>(5);
    const { themeStyles, theme } = useTheme();

    const textColor =
        theme === "dark"
            ? `${themeStyles.main.textSubHeadingColor}`
            : `${themeStyles.main.textHeadingColor}`;

    useEffect(() => {
        if (isRefresh) {
            setIsRefresh(false);
            setPaginationModel({
                ...paginationModel,
                page: 0
            });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isRefresh])

    useEffect(() => {
        if (tenantId) {
            getRdRentalCreditCardWithTenantId(tenantId, paginationModel.page, paginationModel.size)
                .then(res => {
                    setTotalLength(res?.count);
                    setCreditCardData(res?.rdRentalCreditCard);
                    handleTenantError(null);
                })
                .catch(error => {
                    handleTenantError(error);
                });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tenantId, paginationModel])


    const addCreditCard = () => setShowPopupAddNewCreditCard(true);

    const isValidFormat = (dateStr: string) => {
        const regex = /^(0[1-9]|1[0-2])\/\d{2}$/;
        return regex.test(dateStr);
    };

    const isValidExpirationDate = (dateStr: string) => {
        if (!isValidFormat(dateStr)) {
            return false;
        }

        const [monthStr, yearStr] = dateStr.split('/');
        const month = parseInt(monthStr, 10);
        const year = parseInt(yearStr, 10);

        const now = new Date();
        const currentMonth = now.getMonth() + 1;
        const currentYear = now.getFullYear() % 100;

        if (year < currentYear || (year === currentYear && month < currentMonth)) {
            return false;
        }

        return true;
    };

    const deleteCreditCard = (primaryKey: number) => {
        setDeleteData({
            id: primaryKey,
            tilte: `Delete`,
            summary: `Are you sure you want to delete this Credit Card?`,
            isDelete: (callback: any) => {
                delete_RdRentalCreditCard(primaryKey, auth?.id)
                    .then(() => {
                        setIsRefresh(true);
                        handleTenantError(null);
                        callback();
                    })
                    .catch(error => {
                        handleTenantError(error);
                    })
            }
        });

        setDeletePopup(true);
    }

    const updateCreditCard = (creditCardDetails: any) => {
        put_RdRentalCreditCard(creditCardDetails, auth?.id)
            .then(() => {
                setCreditCardData(prevData =>
                    prevData.map(card =>
                        card.id === creditCardDetails?.id
                            ? { ...card, isActive: creditCardDetails?.isActive }
                            : card
                    )
                );

                handleTenantError(null);
            })
            .catch(error => {
                handleTenantError(error);
            })
    }

    return (
        <div className="w-full border border-[#167AC3] rounded-lg">
            <div className="flex justify-between items-center w-full h-[50px] px-4 py-2 bg-[#167AC3] text-white rounded-tl-lg rounded-tr-lg">
                <h4 className="font-medium text-white">
                    Credit Cards
                </h4>
            </div>

            <div className="flex flex-col p-4 gap-3">
                <table className="w-full">
                    <thead className="border-b-2 border-[#167AC3]">
                        <tr className="text-sm text-left">
                            {heading.map((item, index) => (
                                <th
                                    key={index}
                                    className={`border-r ${themeStyles.main.borderColor} text-left p-0.5 ${textColor}`}
                                >
                                    {item}
                                </th>
                            ))}
                            <th className="text-left px-1 py-0.5 text-blue-500 font-bold select-none"></th>
                        </tr>
                    </thead>

                    <tbody className="border-b border-[#167AC3]">
                        {creditCardData?.map((item, index) => (
                            <tr
                                key={index}
                                className={`border-r ${themeStyles.main.borderColor} text-left text-sm p-0.5 ${textColor}`}
                            >
                                <td className={`border-r ${themeStyles.main.borderColor} border-b ${themeStyles.main.borderColor} text-left text-sm p-0.5 ${textColor}`}>
                                    {item?.id}
                                </td>

                                <td className={`border-r ${themeStyles.main.borderColor} border-b ${themeStyles.main.borderColor} text-left text-sm p-0.5 ${textColor}`}>
                                    {item?.cardholderName}
                                </td>

                                <td className={`border-r ${themeStyles.main.borderColor} border-b ${themeStyles.main.borderColor} text-left text-sm p-0.5 ${textColor}`}>
                                    {item?.expirationDate}
                                </td>

                                <td className={`border-r ${themeStyles.main.borderColor} border-b ${themeStyles.main.borderColor} text-left text-sm p-0.5 ${textColor}`}>
                                    {item?.creditCardNumber?.slice(-4)}
                                </td>

                                <td className={`border-r ${themeStyles.main.borderColor} border-b ${themeStyles.main.borderColor} text-left text-sm p-0.5 ${textColor}`}>
                                    <button
                                        className={`p-2 rounded-md text-white ${item?.isActive && isValidExpirationDate(item?.expirationDate) ? 'bg-[#16a34a]' : 'bg-red-500'} cursor-pointer`}
                                        onClick={() => updateCreditCard({ ...item, isActive: !item?.isActive })}
                                        disabled={!isValidExpirationDate(item?.expirationDate)}
                                    >
                                        {item?.isActive && isValidExpirationDate(item?.expirationDate)
                                            ? "Yes"
                                            : "No"}
                                    </button>
                                </td>

                                <td
                                    className={`text-[#3490dc] font-bold cursor-pointer select-none border-r ${themeStyles.main.borderColor} border-b ${themeStyles.main.borderColor} text-left text-sm p-0.5 ${themeStyles.main.textSubHeadingColor}`}
                                    onClick={() => deleteCreditCard(item?.id)}
                                >
                                    {"X"}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <Pagination
                    totalLength={totalLength}
                    paginationModel={paginationModel}
                    setPaginationModel={setPaginationModel}
                    setIsRefresh={setIsRefresh}
                    showEntries={showEntries}
                    setShowEntries={setShowEntries}
                />

                <div>
                    <button
                        className="bg-[#167AC3] rounded-md py-1 px-4 font-medium text-white cursor-pointer"
                        onClick={addCreditCard}
                    >
                        Add New
                    </button>
                </div>
            </div>
        </div>
    );
}

export default CreditCardDetails